/**
 * Maps an unknown error (typically from axios) to a Growl notification payload.
 *
 * Rules:
 * - Canceled requests  → null (no toast; user-initiated).
 * - HTTP 401           → null (the axios interceptor handles refresh silently).
 * - All other errors   → { title, description?, type }.
 *
 * 4xx statuses use type 'warning'; 5xx / network / timeout use type 'error'.
 * Strings are resolved via i18n with English defaultValues so the function
 * works even before translations are loaded.
 */

import axios from 'axios';
import { i18nMessage } from './i18nMessage';
import { GrowlType } from '../components/common/Growl';

export type HttpErrorPayload = {
  title: string;
  description?: string;
  type: GrowlType;
};

/**
 * Translate via the global i18n instance (safe outside React). Shared with the
 * CEO Pricing mutation layer so the uninitialised-i18next contract has one
 * implementation, and `params` reach BOTH the resource and the fallback.
 */
const t = (
  key: string,
  defaultValue: string,
  params?: Readonly<Record<string, unknown>>,
): string => i18nMessage(key, defaultValue, params);

export function mapHttpError(error: unknown): HttpErrorPayload | null {
  // --- Canceled requests: swallow silently ---
  if (axios.isCancel(error)) return null;
  if (
    axios.isAxiosError(error) &&
    (error.code === 'ERR_CANCELED' || axios.isCancel(error))
  ) {
    return null;
  }

  if (axios.isAxiosError(error)) {
    // --- Timeout: code ECONNABORTED or status 408 ---
    if (
      error.code === 'ECONNABORTED' ||
      error.response?.status === 408
    ) {
      return {
        title: t('errors.http.timeout', 'Request timed out'),
        description: t(
          'errors.http.timeoutDesc',
          'The server took too long to respond. Please try again.',
        ),
        type: 'error',
      };
    }

    // --- Network/CORS: no response at all, or ERR_NETWORK ---
    if (!error.response || error.code === 'ERR_NETWORK') {
      return {
        title: t('errors.http.network', "Can't reach the server"),
        description: t(
          'errors.http.networkDesc',
          'You appear to be offline or the request was blocked.',
        ),
        type: 'error',
      };
    }

    const status = error.response.status;

    // 401 — silently handled by the interceptor
    if (status === 401) return null;

    // Map known statuses
    switch (status) {
      case 400:
        return {
          title: t('errors.http.badRequest', 'Bad request'),
          description: t(
            'errors.http.badRequestDesc',
            'The request contained invalid data.',
          ),
          type: 'warning',
        };
      case 403:
        return {
          title: t('errors.http.forbidden', 'No permission'),
          description: t(
            'errors.http.forbiddenDesc',
            'You do not have permission to perform this action.',
          ),
          type: 'warning',
        };
      case 404:
        return {
          title: t('errors.http.notFound', 'Not found'),
          description: t(
            'errors.http.notFoundDesc',
            'The requested resource could not be found.',
          ),
          type: 'warning',
        };
      case 409:
        return {
          title: t('errors.http.conflict', 'Conflict'),
          description: t(
            'errors.http.conflictDesc',
            'The request conflicts with the current state of the resource.',
          ),
          type: 'warning',
        };
      case 422:
        return {
          title: t('errors.http.unprocessable', 'Validation failed'),
          description: t(
            'errors.http.unprocessableDesc',
            'The submitted data failed validation. Please check your input.',
          ),
          type: 'warning',
        };
      case 429:
        return {
          title: t('errors.http.tooManyRequests', 'Too many requests'),
          description: t(
            'errors.http.tooManyRequestsDesc',
            'You are sending requests too quickly. Please slow down.',
          ),
          type: 'warning',
        };
      case 500:
        return {
          title: t('errors.http.serverError', 'Server error'),
          description: t(
            'errors.http.serverErrorDesc',
            'An unexpected error occurred on the server.',
          ),
          type: 'error',
        };
      case 502:
        return {
          title: t('errors.http.badGateway', 'Bad gateway'),
          description: t(
            'errors.http.badGatewayDesc',
            'The server received an invalid response from an upstream server.',
          ),
          type: 'error',
        };
      case 503:
        return {
          title: t('errors.http.serviceUnavailable', 'Service unavailable'),
          description: t(
            'errors.http.serviceUnavailableDesc',
            'The server is temporarily unavailable. Please try again later.',
          ),
          type: 'error',
        };
      case 504:
        return {
          title: t('errors.http.gatewayTimeout', 'Gateway timed out'),
          description: t(
            'errors.http.gatewayTimeoutDesc',
            'The upstream server did not respond in time.',
          ),
          type: 'error',
        };
      default: {
        // Unknown HTTP status
        const typeForStatus: GrowlType = status >= 500 ? 'error' : 'warning';
        return {
          // `{{status}}`, not a template literal: the en-US/zh-CN resources
          // carry the placeholder, and an initialised i18next renders the
          // resource — a pre-substituted default would never be used, so the
          // param has to travel with the call.
          title: t('errors.http.unknown', 'Something went wrong (HTTP {{status}})', { status }),
          type: typeForStatus,
        };
      }
    }
  }

  // --- Non-axios Error ---
  if (error instanceof Error) {
    return {
      title: t('errors.http.generic', 'Something went wrong'),
      description: error.message,
      type: 'error',
    };
  }

  // --- Completely unknown ---
  return {
    title: t('errors.http.generic', 'Something went wrong'),
    type: 'error',
  };
}
